/**
 * Hakediş dönemi hesapları — ekranın yazdığı her tarih backend'in kullandığı kuralla AYNI
 * olmak zorunda, yoksa panel "hesaplandı" derken kayıt ortada olmaz.
 *
 * Backend kuralı (TimeUtcProvider):
 *  - Dönem = TAMAMLANMIŞ ISO hafta (Pazartesi 00:00 → Pazar 23:59:59, TR yerel).
 *  - Kapanış job'ları: filo Pazartesi 07:00, kurye Pazartesi 08:00 (Türkiye saati).
 *  - Türkiye yıl boyu UTC+3 (yaz saati uygulaması yok) → sabit ofsetle hesaplamak güvenli.
 */

const TR_OFFSET_MS = 3 * 60 * 60 * 1000;

/** Şu anın TR duvar saati; alanları getUTC* ile okunur. */
function trNow(now = Date.now()) {
    return new Date(now + TR_OFFSET_MS);
}

/** TR duvar saatindeki bir anı gerçek UTC epoch'a çevirir. */
function trWallToEpoch(year, monthIndex, day, hour = 0, minute = 0) {
    return Date.UTC(year, monthIndex, day, hour, minute, 0) - TR_OFFSET_MS;
}

/** Pazartesi başlangıçlı gün indeksi (Pzt=0 … Paz=6). */
function mondayIndex(date) {
    return (date.getUTCDay() + 6) % 7;
}

/** Verilen anın ait olduğu ISO haftanın Pazartesi'si (TR duvar saati, 00:00). */
export function weekStartOf(now = Date.now()) {
    const tr = trNow(now);
    const start = new Date(tr);
    start.setUTCDate(tr.getUTCDate() - mondayIndex(tr));
    start.setUTCHours(0, 0, 0, 0);
    return start;
}

/** ISO hafta numarası — backend ISOWeek ile aynı tanım (Perşembe kuralı). */
export function isoWeekOf(now = Date.now()) {
    const tr = trNow(now);
    const thursday = new Date(Date.UTC(tr.getUTCFullYear(), tr.getUTCMonth(), tr.getUTCDate()));
    thursday.setUTCDate(thursday.getUTCDate() - mondayIndex(thursday) + 3);
    const firstThursday = new Date(Date.UTC(thursday.getUTCFullYear(), 0, 4));
    firstThursday.setUTCDate(firstThursday.getUTCDate() - mondayIndex(firstThursday) + 3);
    const week = 1 + Math.round((thursday - firstThursday) / (7 * 24 * 60 * 60 * 1000));
    return { year: thursday.getUTCFullYear(), week };
}

/**
 * Bir sonraki kapanış anı ve o an kapanacak hafta.
 * @param {number} hour Kapanış saati (TR) — filo 7, kurye 8.
 */
export function nextClosing(hour, now = Date.now()) {
    const tr = trNow(now);
    let daysUntilMonday = (7 - mondayIndex(tr)) % 7;

    // Pazartesi ve saat geçtiyse bu haftanın kapanışı olmuş demektir → gelecek Pazartesi.
    if (daysUntilMonday === 0 && (tr.getUTCHours() > hour || (tr.getUTCHours() === hour && tr.getUTCMinutes() > 0))) {
        daysUntilMonday = 7;
    }

    const target = new Date(tr);
    target.setUTCDate(tr.getUTCDate() + daysUntilMonday);

    const epoch = trWallToEpoch(target.getUTCFullYear(), target.getUTCMonth(), target.getUTCDate(), hour);

    // Kapanışta işlenen dönem: kapanış gününden bir gün öncesinin (Pazar) haftası.
    const closingWeek = isoWeekOf(epoch - 24 * 60 * 60 * 1000);

    return { epoch, ...closingWeek };
}

/** "2 gün 4 saat" / "3 saat 20 dk" / "birkaç dakika" — sıfır birimleri yazmaz. */
export function humanizeUntil(targetEpoch, now = Date.now()) {
    const diff = targetEpoch - now;
    if (diff <= 0) return 'birazdan';

    const totalMinutes = Math.floor(diff / 60000);
    const days = Math.floor(totalMinutes / 1440);
    const hours = Math.floor((totalMinutes % 1440) / 60);
    const minutes = totalMinutes % 60;

    if (days > 0) return hours > 0 ? `${days} gün ${hours} saat` : `${days} gün`;
    if (hours > 0) return minutes > 0 ? `${hours} saat ${minutes} dk` : `${hours} saat`;
    return `${minutes} dk`;
}

const TR_MONTHS = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];

/** "27 Tem – 2 Ağu" biçiminde hafta aralığı. */
export function formatWeekRange(start = weekStartOf()) {
    const end = new Date(start);
    end.setUTCDate(start.getUTCDate() + 6);

    const left = `${start.getUTCDate()} ${TR_MONTHS[start.getUTCMonth()]}`;
    const right = `${end.getUTCDate()} ${TR_MONTHS[end.getUTCMonth()]}`;
    return `${left} – ${right}`;
}

/**
 * Akış hattında ŞU AN hangi adımın yandığı. Hafta içi dönem toplanır; Pazartesi 07:00–09:00
 * arası kapanış koşar. Sabitlemek yerine saate bakmak, bandın "süs" değil durum göstergesi
 * olmasını sağlar.
 */
export function currentFlowStep(now = Date.now()) {
    const tr = trNow(now);
    const isMonday = mondayIndex(tr) === 0;
    const hour = tr.getUTCHours();

    if (isMonday && hour >= 7 && hour < 9) return 'close';
    return 'week';
}

/** Akış adımlarının sırası — "geçilmiş / şu anki / sıradaki" ayrımı buradan okunur. */
export const FLOW_ORDER = ['week', 'close', 'invoice', 'approve', 'paid'];

/** İki ISO haftasını karşılaştırır: negatif = a önce, 0 = aynı, pozitif = a sonra. */
export function compareWeeks(a, b) {
    if (a.year !== b.year) return a.year - b.year;
    return a.week - b.week;
}

/**
 * SEÇİLİ haftanın akıştaki adımı.
 *
 * Takvim saati tek başına YETMEZ: kullanıcı geçmiş bir haftaya baktığında bant "Hafta sürüyor"
 * yanmaya devam ediyor, alttaki liste kapanmış dönemi gösteriyordu — iki bilgi birbiriyle
 * çelişiyordu. Dönem kapandıktan SONRAKİ adımı takvim değil, hakediş kaydının DURUMU söyler;
 * PaymentStatus zaten bandın adımlarıyla birebir örtüşür.
 *
 * @param {{year:number, week:number, status:number|null}} ctx Seçili hafta ve o haftanın
 *        hakediş durumu (kayıt yoksa status null).
 */
export function weekFlowStep(ctx, now = Date.now()) {
    if (!ctx) return currentFlowStep(now);

    const cmp = compareWeeks(ctx, isoWeekOf(now));

    // Yürüyen ya da henüz başlamamış hafta: kapanış öncesi.
    if (cmp >= 0) return currentFlowStep(now);

    // Kapanmış hafta, kayıt yok → dönem kapanışında satır üretilmemiş (ör. o hafta hiç
    // teslimat/mesai yok). Akış "kapanış" adımında durur; ileriye gitmez.
    if (ctx.status == null) return 'close';

    switch (ctx.status) {
        case 0: return 'invoice';   // WaitingForInvoice
        case 4: return 'invoice';   // Rejected → yeniden fatura yüklenecek
        case 1: return 'approve';   // PendingApproval
        case 2: return 'paid';      // Approved → ödeme sırada
        case 3: return 'paid';      // Paid → akış tamamlandı
        default: return 'close';
    }
}

/** Seçili hafta için akış TAMAMLANDI mı (hakediş ödendi). */
export function isFlowComplete(ctx) {
    return !!ctx && ctx.status === 3;
}

/** "Pazartesi 07:00" gibi kapanış etiketi. */
export function formatClosing(epoch) {
    const tr = trNow(epoch);
    const day = tr.getUTCDate();
    const month = TR_MONTHS[tr.getUTCMonth()];
    const hh = String(tr.getUTCHours()).padStart(2, '0');
    return `${day} ${month} Pazartesi ${hh}:00`;
}
